import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarRange, Archive, CalendarPlus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Term boundary nudge — archive last term's classes, or add the new term's schedule.
export default function TermAlert({ onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [setupDate, setSetupDate] = useState("");

  const load = useCallback(() => {
    api.get("/terms/status").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const dismiss = async () => {
    const key = data?.key;
    setData({ action: null });
    if (key) api.post("/terms/dismiss", { key }).catch(() => {});
  };

  const archive = async () => {
    setBusy(true);
    try {
      const r = await api.post("/terms/archive", { term_name: data?.term?.name || "" });
      toast.success(`Archived ${r.data.archived} classes — history kept. Ready for a fresh term!`);
      setData({ action: null });
      onChanged?.();
    } catch { toast.error("Could not archive"); }
    setBusy(false);
  };

  const clearAll = async () => {
    if (!window.confirm("Permanently delete this term's classes? Your rewards, streaks and history are kept — but the classes won't be recoverable (use Archive to keep a copy).")) return;
    setBusy(true);
    try {
      await api.post("/terms/clear");
      toast.success("Cleared this term's classes.");
      setData({ action: null });
      onChanged?.();
    } catch { toast.error("Could not clear"); }
    setBusy(false);
  };

  const addClasses = () => {
    window.dispatchEvent(new Event("open-schedule-builder"));
    dismiss();
  };

  const saveSetup = async () => {
    if (!setupDate) { toast.error("Pick your term end date."); return; }
    setBusy(true);
    try {
      await api.put("/semester", { end_date: setupDate });
      toast.success("Got it — I'll remind you when the term wraps up.");
      setData({ action: null });
    } catch { toast.error("Could not save"); }
    setBusy(false);
  };

  if (!data || !data.action) return null;

  const term = data.term || {};

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, height: 0 }}
        data-testid="term-alert"
        className={`mb-5 rounded-2xl border p-5 ${
          data.action === "end" ? "border-[#F59E0B]/40 bg-gradient-to-br from-[#F59E0B]/[0.08] to-[#F59E0B]/[0.02]"
          : "border-brand/25 bg-gradient-to-br from-brand/[0.06] to-brand/[0.02]"
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-1.5 mono text-[11px] tracking-[0.2em] uppercase" style={{ color: data.action === "end" ? "#92400e" : undefined }}>
            {data.action === "end" ? <CalendarRange size={13} strokeWidth={1.8} /> : <Sparkles size={13} strokeWidth={1.8} className="text-brand" />}
            <span className={data.action === "end" ? "" : "text-brand"}>
              {data.action === "end" ? "Term wrapping up" : data.action === "start" ? "New term" : "Set your term"}
            </span>
          </div>
          <button data-testid="term-alert-dismiss" onClick={dismiss} aria-label="Dismiss"
            className="p-1.5 -m-1 rounded-md text-muted-foreground hover:bg-black/5 transition-colors shrink-0">
            <X size={16} strokeWidth={1.6} />
          </button>
        </div>

        {data.action === "end" && (
          <>
            <h2 className="font-display text-xl font-semibold mt-1">
              {term.name || "This term"} is wrapping up{data.end_label ? ` (ends ${data.end_label})` : ""}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Archive this term's classes to start fresh — your streaks, check-ins and mileage all stay.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button data-testid="term-archive-btn" onClick={archive} disabled={busy}
                className="inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                <Archive size={15} strokeWidth={1.8} /> Archive &amp; keep history
              </button>
              <button data-testid="term-clear-btn" onClick={clearAll} disabled={busy}
                className="text-sm font-semibold text-muted-foreground hover:text-destructive transition-colors px-2 py-2">
                Clear instead
              </button>
            </div>
          </>
        )}

        {data.action === "start" && (
          <>
            <h2 className="font-display text-xl font-semibold mt-1">
              {term.name ? `${term.name} has started 🎉` : "A new term has started 🎉"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Add your classes{data.start_label ? ` (term began ${data.start_label})` : ""} — paste them in and jeeo maps your routes & gap time instantly.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button data-testid="term-add-btn" onClick={addClasses}
                className="inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
                <CalendarPlus size={15} strokeWidth={1.8} /> Add my schedule
              </button>
            </div>
          </>
        )}

        {data.action === "ask_setup" && (
          <>
            <h2 className="font-display text-xl font-semibold mt-1">When does your term end?</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Set it once and jeeo will remind you to archive and refresh your schedule at the right time.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <input data-testid="term-setup-date" type="date" value={setupDate} onChange={(e) => setSetupDate(e.target.value)}
                className="bg-white border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
              <button data-testid="term-setup-save" onClick={saveSetup} disabled={busy}
                className="bg-brand text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
                Save
              </button>
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
